import React, { useRef, useState, useEffect } from "react";
import { Cropper } from "react-cropper";
import { Dialog } from "@radix-ui/react-dialog";
import { Button } from "./buttons/button/Button";
import { cn } from "./utils/cn";
import { ImageUpload } from "../types";

interface ImageCropperDialogProps {
  open: boolean;
  onClose: () => void;
  image: string;
  onCrop: (croppedImage: string) => void;
  aspectRatio?: number;
  imageFormat?: string;
}

export const ImageCropperDialog: React.FC<ImageCropperDialogProps> = ({
  open,
  onClose,
  image,
  onCrop,
  aspectRatio,
  imageFormat = "image/png",
}) => {
  const cropperRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>("");
  const [localimageSrc, setLocalImageSrc] = useState<string>("");

  useEffect(() => {
    // Add cropper CSS from CDN
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.5.13/cropper.min.css";
    document.head.appendChild(link);

    return () => {
      // Clean up the CSS when component unmounts
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (image) {
      // Reconstruct the data URL
      setImageSrc(`data:image/png;base64,${image}`);
    }
  }, [image]);

  const handleCrop = () => {
    if (cropperRef.current) {
      const cropper = (cropperRef.current as any).cropper;
      if (cropper) {
        setIsLoading(true);
        const croppedCanvas = cropper.getCroppedCanvas();
        const croppedImage = croppedCanvas.toDataURL(imageFormat);
        onCrop(croppedImage);
        setIsLoading(false);
        onClose();
      }
    }
  };

  if (!imageSrc) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 w-[90%] max-w-2xl">
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold">Crop Image</h2>
            <Button
              onClick={() =>
                document.getElementById("logo-uploader-cropping-tool")?.click()
              }
              variant="primary"
            >
              Upload new image
            </Button>
          </div>
          <div className="mb-4">
            <Cropper
              src={localimageSrc === "" ? imageSrc : localimageSrc}
              style={{ height: 400, width: "100%" }}
              guides={true}
              ref={cropperRef}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCrop} disabled={isLoading}>
              {isLoading ? "Cropping..." : "Crop & Save"}
            </Button>
          </div>
        </div>
      </div>
      <input
        className="hidden"
        type="file"
        id={"logo-uploader-cropping-tool"}
        onChange={(event) => {
          if (event.target.files === null || event.target.files.length === 0)
            return;

          const file = event.target.files[0];
          if (!file) return;

          const reader = new FileReader();

          reader.onload = (e: ProgressEvent<FileReader>) => {
            const formatInfo = (e.target?.result as string).split(",")[0];
            const base64String = (e.target?.result as string).split(",")[1];
            const format = formatInfo.split(";")[0].split(":")[1];

            const imageUpload: ImageUpload = {
              fileType: formatInfo,
              base64File: base64String,
            };
            setLocalImageSrc(`data:image/png;base64,${base64String}`);
          };

          reader.readAsDataURL(file);
        }}
      ></input>
    </Dialog>
  );
};
